import { useCallback, useEffect } from 'react'

import authReturnUrlService from '@app/services/authReturnUrlService'
import { useJwtSession } from '@app/hooks/useJwtSession'

import { getJwtExpirationTime, isJwtExpired } from './utils'

export const useJwtExpirationGuard = (): void => {
  const { session, clearSession } = useJwtSession()

  const handleExpiredSession = useCallback((): void => {
    if (!session || !isJwtExpired(session.token)) return

    authReturnUrlService.setReturnUrl(window.location.pathname + window.location.search)
    clearSession()
  }, [clearSession, session])

  useEffect(() => {
    handleExpiredSession()
  }, [handleExpiredSession])

  useEffect(() => {
    if (!session) return

    const expirationTime = getJwtExpirationTime(session.token)

    if (!expirationTime) return

    const delay = Math.max(expirationTime - Date.now(), 0)
    const timeout = window.setTimeout(handleExpiredSession, delay)

    return () => window.clearTimeout(timeout)
  }, [handleExpiredSession, session])

  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (document.visibilityState !== 'visible') return

      handleExpiredSession()
    }

    window.addEventListener('focus', handleExpiredSession)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('focus', handleExpiredSession)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [handleExpiredSession])
}
